// Compat shims for `next/navigation` hooks used in ported primitives.
// Each shim wraps the equivalent TanStack Router hook and exposes the
// Next.js return shape so call-sites stay unchanged.
//
//   useRouter() → { push, replace, back, forward, refresh, prefetch }
//   usePathname() → string
//   useSearchParams() → URLSearchParams (read-only against window.location)
import { useNavigate, useRouterState } from "@tanstack/react-router";

interface NextRouterOptions {
  scroll?: boolean;
}

export function useRouter() {
  const navigate = useNavigate();
  return {
    push: (href: string, _options?: NextRouterOptions) => navigate({ to: href }),
    replace: (href: string, _options?: NextRouterOptions) =>
      navigate({ to: href, replace: true }),
    back: () => window.history.back(),
    forward: () => window.history.forward(),
    refresh: () => window.location.reload(),
    prefetch: (_href: string) => {
      /* TanStack handles preload via Link `preload="intent"` */
    },
  };
}

export function usePathname(): string {
  const router = useRouterState();
  return router.location.pathname;
}

export function useSearchParams(): URLSearchParams {
  const router = useRouterState();
  // Reactive on URL change because router state updates.
  return new URLSearchParams(router.location.searchStr);
}

// Next.js useParams returns the dynamic segments as a plain record. TanStack
// route params live behind `useParams({ from })`; without the route key, fall
// back to parsing pathname against router state.
export function useParams<T = Record<string, string>>(): T {
  const router = useRouterState();
  return (router.matches.at(-1)?.params ?? {}) as T;
}
